import { useState, useEffect } from 'react';
import { useWorkspace } from '../../hooks/useWorkspace';
import { apiClient } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';

interface Snapshot {
  id: string;
  description: string;
  frameworks: string[];
  createdAt: string;
  createdBy: string;
  totalDeliverables: number;
  completionPercent: number;
  exportableCount: number;
}

interface ActiveFramework {
  frameworkId: string;
  name: string;
}

const FRAMEWORK_BADGE_VARIANTS = [
  'brand',
  'success',
  'info',
  'warning',
  'danger',
] as const;

export function SnapshotList() {
  const { currentWorkspace } = useWorkspace();
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [description, setDescription] = useState('');
  const [selectedFrameworks, setSelectedFrameworks] = useState<string[]>([]);
  const [activeFrameworks, setActiveFrameworks] = useState<ActiveFramework[]>([]);
  const [creating, setCreating] = useState(false);
  const [creatingLink, setCreatingLink] = useState<string | null>(null);

  const plan = currentWorkspace?.plan;
  const isProPlus = plan === 'pro' || plan === 'enterprise';

  useEffect(() => {
    if (!currentWorkspace) return;
    fetchSnapshots();
  }, [currentWorkspace]);

  async function fetchSnapshots() {
    if (!currentWorkspace) return;
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await apiClient.get<Snapshot[]>(
      `/api/tenants/${currentWorkspace.tenantId}/snapshots`
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setSnapshots(data);
    }
    setLoading(false);
  }

  async function fetchActiveFrameworks() {
    if (!currentWorkspace) return;

    const { data } = await apiClient.get<ActiveFramework[]>(
      `/api/tenants/${currentWorkspace.tenantId}/frameworks`
    );

    if (data) {
      setActiveFrameworks(data);
    }
  }

  function handleOpenCreate() {
    setDescription('');
    setSelectedFrameworks([]);
    setError(null);
    fetchActiveFrameworks();
    setShowCreate(true);
  }

  function handleCloseCreate() {
    setShowCreate(false);
    setDescription('');
    setSelectedFrameworks([]);
  }

  function toggleFramework(fwId: string) {
    setSelectedFrameworks((prev) =>
      prev.includes(fwId) ? prev.filter((id) => id !== fwId) : [...prev, fwId]
    );
  }

  async function handleCreateSnapshot() {
    if (!currentWorkspace) return;
    if (!description.trim()) {
      setError('Description is required');
      return;
    }
    if (selectedFrameworks.length === 0) {
      setError('Select at least one framework');
      return;
    }

    setCreating(true);
    setError(null);

    const { data, error: apiError } = await apiClient.post<Snapshot>(
      `/api/tenants/${currentWorkspace.tenantId}/snapshots`,
      { description: description.trim(), frameworkIds: selectedFrameworks }
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setSnapshots((prev) => [data, ...prev]);
      handleCloseCreate();
    }
    setCreating(false);
  }

  async function handleCreateAuditorLink(snapshotId: string) {
    if (!currentWorkspace) return;
    setCreatingLink(snapshotId);

    const { data, error: apiError } = await apiClient.post<{ token: string }>(
      `/api/tenants/${currentWorkspace.tenantId}/auditor-links`,
      { snapshotId, label: 'Quick Link', expiresInDays: 30 }
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      const url = `${window.location.origin}/auditor/${data.token}`;
      await navigator.clipboard.writeText(url);
    }
    setCreatingLink(null);
  }

  function formatDate(dateStr: string) {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  }

  function getFrameworkBadgeVariant(index: number) {
    return FRAMEWORK_BADGE_VARIANTS[index % FRAMEWORK_BADGE_VARIANTS.length];
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="w-8 h-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (!isProPlus) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-white">
            Compliance Snapshots
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Point-in-time compliance records for audit readiness
          </p>
        </div>

        <Card>
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-brand-light" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 15V3m0 12l-4-4m4 4l4-4" />
                <path d="M2 17l.621 2.485A2 2 0 004.561 21h14.878a2 2 0 001.94-1.515L22 17" />
              </svg>
            </div>
            <h2 className="text-lg font-display font-semibold text-white mb-2">
              Pro+ Feature
            </h2>
            <p className="text-slate-400 text-sm max-w-md mx-auto mb-6">
              Compliance Snapshots are available on Pro and Enterprise plans. Upgrade to
              create point-in-time compliance records for auditors and stakeholders.
            </p>
            <Button>Upgrade Plan</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-white">
            Compliance Snapshots
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {snapshots.length} snapshot{snapshots.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button onClick={handleOpenCreate}>Create Snapshot</Button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Snapshot list */}
      {snapshots.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-slate-400 text-sm">No snapshots created yet.</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-4"
              onClick={handleOpenCreate}
            >
              Create Your First Snapshot
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {snapshots.map((snapshot) => {
            const isExpanded = expandedId === snapshot.id;

            return (
              <div
                key={snapshot.id}
                className="bg-surface-mid rounded-xl border border-border overflow-hidden"
              >
                {/* Snapshot row */}
                <div
                  className="px-6 py-4 cursor-pointer hover:bg-surface-light/30 transition-colors"
                  onClick={() =>
                    setExpandedId(isExpanded ? null : snapshot.id)
                  }
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white">
                        {snapshot.description}
                      </p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {snapshot.frameworks.map((fw, i) => (
                          <Badge
                            key={fw}
                            variant={getFrameworkBadgeVariant(i)}
                          >
                            {fw}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm text-slate-400">
                        {formatDate(snapshot.createdAt)}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        by {snapshot.createdBy}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-6 py-4 border-t border-border bg-surface-dark/30">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                      <div className="bg-surface-mid rounded-lg p-3 border border-border">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                          Total Deliverables
                        </p>
                        <p className="text-xl font-display font-bold text-white">
                          {snapshot.totalDeliverables}
                        </p>
                      </div>
                      <div className="bg-surface-mid rounded-lg p-3 border border-border">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                          Completion
                        </p>
                        <p className="text-xl font-display font-bold text-white">
                          {snapshot.completionPercent}%
                        </p>
                      </div>
                      <div className="bg-surface-mid rounded-lg p-3 border border-border">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                          Exportable
                        </p>
                        <p className="text-xl font-display font-bold text-white">
                          {snapshot.exportableCount}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button variant="secondary" size="sm">
                        View Details
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={creatingLink === snapshot.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateAuditorLink(snapshot.id);
                        }}
                      >
                        Create Auditor Link
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Snapshot Modal */}
      <Modal
        isOpen={showCreate}
        onClose={handleCloseCreate}
        title="Create Snapshot"
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseCreate}>
              Cancel
            </Button>
            <Button loading={creating} onClick={handleCreateSnapshot}>
              Create Snapshot
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-slate-300">
              Description
            </label>
            <textarea
              className="w-full px-3 py-2 bg-surface-dark border border-border rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand transition-colors resize-none"
              rows={3}
              placeholder="e.g. Q1 2026 Compliance Review"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-slate-300">
              Frameworks
            </label>
            {activeFrameworks.length === 0 ? (
              <p className="text-xs text-slate-500">
                No active frameworks found. Activate frameworks first.
              </p>
            ) : (
              <div className="space-y-2">
                {activeFrameworks.map((fw) => (
                  <label
                    key={fw.frameworkId}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-surface-light/30 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedFrameworks.includes(fw.frameworkId)}
                      onChange={() => toggleFramework(fw.frameworkId)}
                      className="w-4 h-4 rounded border-border bg-surface-dark text-brand focus:ring-brand/50"
                    />
                    <span className="text-sm text-white">
                      {fw.name || fw.frameworkId}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
